// React hook for desktop notifications
import { useState, useEffect, useCallback } from 'react';
import {
  isNotificationSupported,
  getNotificationPermission,
  requestNotificationPermission,
  areNotificationsEnabled,
  saveNotificationPreference,
  getNotificationPreference,
  notifyNewMessage,
  notifyUpcomingDeadline,
  notifyClassStarting,
  notifyNewAssignment,
  type NotificationPermissionStatus
} from '@/lib/notifications';

export function useDesktopNotifications() {
  const [permission, setPermission] = useState<NotificationPermissionStatus>(getNotificationPermission());
  const [isEnabled, setIsEnabled] = useState(getNotificationPreference());
  const [isSupported] = useState(isNotificationSupported());

  // Request permission
  const requestPermission = useCallback(async () => {
    const result = await requestNotificationPermission();
    setPermission(result);
    if (result === 'granted') {
      setIsEnabled(true);
      saveNotificationPreference(true);
    }
    return result;
  }, []);

  // Toggle notifications on/off
  const toggleNotifications = useCallback((enabled: boolean) => {
    setIsEnabled(enabled);
    saveNotificationPreference(enabled);
  }, []);

  // Check if notifications are fully enabled
  const canNotify = isSupported && permission === 'granted' && isEnabled;

  // Notification helpers
  const sendMessageNotification = useCallback((senderName: string, messagePreview: string) => {
    if (!canNotify) return null;
    return notifyNewMessage(senderName, messagePreview);
  }, [canNotify]);

  const sendDeadlineNotification = useCallback((taskTitle: string, dueDate: string) => {
    if (!canNotify) return null;
    return notifyUpcomingDeadline(taskTitle, dueDate);
  }, [canNotify]);

  const sendClassNotification = useCallback((className: string, minutesUntil: number) => {
    if (!canNotify) return null;
    return notifyClassStarting(className, minutesUntil);
  }, [canNotify]);

  const sendAssignmentNotification = useCallback((courseName: string, assignmentTitle: string) => {
    if (!canNotify) return null;
    return notifyNewAssignment(courseName, assignmentTitle);
  }, [canNotify]);

  return {
    isSupported,
    permission,
    isEnabled,
    canNotify,
    requestPermission,
    toggleNotifications,
    notify: {
      message: sendMessageNotification,
      deadline: sendDeadlineNotification,
      class: sendClassNotification,
      assignment: sendAssignmentNotification
    }
  };
}
